import Loading from 'quasar/src/plugins/loading/Loading.js';
import { FindFileItemCommand, FindFilesInfoCommand } from "src/api/Common/Commands/FindFilesInfoCommand";
import { FindFilesInfoResult } from "src/api/data/Common/FindFIlesInfoResult";
import { FileHelper } from "src/extensions/utils/FileHelper";
import { isMatchKeyword } from "src/extensions/utils/KeywordSearch";
import { computed, defineComponent, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { api } from "~/api";
import { GetFilesCommand } from "~/api/Common/Commands/GetFilesCommand";
import { MsgBox } from "~/components/MsgBox";
import { Pagination } from "~/extensions/api";
import { Log } from "~/extensions/utils";
import { FileCode, FileFetchKb, PageID, RoomSb } from "~/extensions/utils/DefineTypes";
import { PagingListHelper } from "~/extensions/utils/PagingHelper";
import { RoomSelectFindConditionViewModel } from "~/pages/common/models/RoomSelectFindConditionViewModel";
import { store as TaikyoStore } from "~/pages/taikyo/store/TaikyoStore";
import { commonStore } from "~/store/CommonStore";
import { myPageStore } from "~/store/MyPageStore";
import { RoomSelectFindConditionValidator } from "../Models/RoomSelectFindConditionValidator";
import { isCurrentlyMovingIn, isMoveIn } from "../Models/RoomSelectHelper";
import BreadcrumbList from "~/components/BreadcrumbList.vue";
import MyPageTable from "src/components/MyPageTable.vue";
import TaikyoRoomPane from "./components/TaikyoRoomPane.vue";
const log = Log.create("BulkTaikyoRoomSelect");
export default defineComponent({
    components: {
        BreadcrumbList,
        MyPageTable,
        TaikyoRoomPane
    },
    name: "BulkTaikyoRoomSelect",
    setup() {
        const router = useRouter();
        const pageName = commonStore.pageName(PageID.taikyoRoomList);
        // 画面上部のタブと選択済みペイン
        const tab = reactive({ value: "entry" });
        const selected = ref([]);
        const confirmPane = ref(false);
        const paneLabels = {
            noRoomLabel: "部屋が選択されていません。"
        };
        // 一時保存一覧
        const saveList = ref([]);
        const isSaveList = computed(() => saveList.value.length > 0);
        // 検索欄
        const condition = ref(new RoomSelectFindConditionViewModel());
        const valid = ref(new RoomSelectFindConditionValidator(condition.value));
        // 部屋一覧
        const sortResults = ref([]);
        const viewResults = ref([]);
        const pagination = ref(new Pagination("nyukyokeiyakuNo", false));
        //テーブルカラム
        const entryColumns = [
            { name: "roomIcon", label: "", align: "left", field: "roomIcon", sortable: false },
            { name: "bukkenName", label: "物件名", align: "left", field: "bukkenName", sortable: false },
            { name: "roomNumber", label: "号室", align: "left", field: "roomNumber", sortable: false },
            { name: "nyukyosha", label: "入居者名", align: "left", field: "nyukyosha", sortable: false }
        ];
        const saveColumns = [
            { name: "tantosha", label: "担当者名", align: "left", field: "tantosha", sortable: false },
            { name: "roomsNumber", label: "部屋数", align: "left", field: "roomsNumber", sortable: false },
            { name: "saveDate", label: "保存日時", align: "left", field: "saveDate", sortable: false },
            { name: "saveDelete", label: "", align: "right", field: "saveDelete", sortable: false }
        ];
        // ファイル関連
        const findFilesInfoCommand = new FindFilesInfoCommand();
        const getFilesCommand = new GetFilesCommand();
        let checkResult = new FindFilesInfoResult();
        // 初期表示処理
        const initView = async () => {
            try {
                Loading.show();
                /** 一時保存一覧情報取得 */
                const res = await api.taikyoNotification.getSaveList();
                viewSelectRoomSort(res);
                pagination.value.rowsNumber = sortResults.value.length;
                viewResults.value = sortResults.value.slice(0, pagination.value.rowsPerPage);
                // ファイル情報取得パラメータ設定
                findFilesInfoCommand.fileList = [new FindFileItemCommand(FileFetchKb.Server, FileCode.TaikyoHasonTemplateFile)];
                // ファイル情報取得
                checkResult = await api.common.findFilesInfo(findFilesInfoCommand);
            }
            catch (ex) {
                log.error("initView", ex);
                MsgBox.error(ex.data?.errorMessage ?? ex.message);
            }
            finally {
                Loading.hide();
            }
        };
        // 新規申請一覧に表示する部屋の処理
        const viewSelectRoomSort = (save) => {
            const orgNyukyoKeiyakuNoList = save.orgNyukyoKeiyakuNo;
            // 新規申請表示ソートで使用する一時保存リストのオリジナル入居契約No.
            const saveOrgNyukyokeiyakuNoList = new Set(orgNyukyoKeiyakuNoList);
            saveList.value = save.saveList;
            // フィルタリング用の条件
            // 入居中：退去解約申請ステータスが0(入居中)
            //        かつ
            //        状況区分が3(入居中)
            const isTargetStatus = (x) => isMoveIn(x.jokyoKb) && isCurrentlyMovingIn(x.taikyoStatus);
            // 一時保存されていない：一時保存リストのオリジナル入居契約No.と一致しない
            const isNotSaveOrgNyukyokeiyakuNo = (x) => !saveOrgNyukyokeiyakuNoList.has(x.orgNyukyokeiyakuNo);
            // 表示前に表示する部屋を絞り、ソートを行う
            sortResults.value = [...myPageStore.personal.nyukyokeiyakuList.filter(x => isTargetStatus(x) && isNotSaveOrgNyukyokeiyakuNo(x))].sort((a, b) => {
                // 物件名（昇順）
                const nameCompare = (a.bukkenName ?? "").localeCompare(b.bukkenName ?? "");
                if (nameCompare !== 0) {
                    return nameCompare;
                }
                // ルーム種別（1 → 2）
                if (a.roomSb !== b.roomSb) {
                    return a.roomSb - b.roomSb;
                }
                // 号室（昇順）
                return Number(a.roomNumber) - Number(b.roomNumber);
            });
            //新規申請可能な入居契約を、退去ストアに保持
            TaikyoStore.selectableRooms = sortResults.value;
        };
        const pagingListHelper = new PagingListHelper({
            mode: "ref",
            list: sortResults,
            pagination,
            load: async () => {
                const res = await api.taikyoNotification.getSaveList();
                viewSelectRoomSort(res);
                pagination.value.rowsNumber = sortResults.value.length;
            },
            resetPagination: () => {
                sortResults.value = [];
            }
        }, log);
        //ページング処理
        const onRequest = async (requestData) => {
            Loading.show();
            await pagingListHelper.paging(requestData);
            const page = requestData?.page ?? pagination.value.page;
            const rowsPerPage = requestData?.rowsPerPage ?? pagination.value.rowsPerPage;
            if (rowsPerPage === 0) {
                viewResults.value = sortResults.value;
            }
            else {
                const start = (page - 1) * rowsPerPage;
                const end = start + rowsPerPage;
                viewResults.value = sortResults.value.slice(start, end);
            }
            Loading.hide();
        };
        // 条件に応じて検索し、条件が空ならリスト全体を表示
        const onSearch = () => {
            if (valid.value.error) {
                return;
            }
            const searchList = sortResults.value.filter(x => isFiltered(x));
            pagination.value.rowsNumber = searchList.length;
            viewResults.value = searchList.slice(0, pagination.value.rowsPerPage);
        };
        /** 対象の入居契約情報がストアにセットされている条件に一致するか判定 */
        function isFiltered(nyukyokeiyaku) {
            const keyword = condition.value.keyword?.trim();
            if (!keyword) {
                return true;
            }
            return isMatchKeyword(nyukyokeiyaku.bukkenName, keyword) || isMatchKeyword(nyukyokeiyaku.roomNumber, keyword) || isMatchKeyword(nyukyokeiyaku.nyukyosha, keyword);
        }
        // 一時保存一覧を削除する処理
        const onSaveDelete = async (bulkTaikyoNo) => {
            const isDelete = await MsgBox.confirm(commonStore.messages.PI0025);
            if (isDelete) {
                try {
                    Loading.show();
                    await api.taikyoNotification.deleteSaveList(bulkTaikyoNo);
                    /** 最新の一時保存一覧情報取得 */
                    const res = await api.taikyoNotification.getSaveList();
                    viewSelectRoomSort(res);
                    pagination.value.rowsNumber = sortResults.value.length;
                    viewResults.value = sortResults.value.slice(0, pagination.value.rowsPerPage);
                }
                catch (ex) {
                    log.error("apply", ex);
                    if (ex?.data?.code === "not_found") {
                        MsgBox.error(ex?.data?.errorMessage ?? ex?.message ?? "エラーが発生しました。");
                    }
                    else {
                        MsgBox.error(ex?.message ?? "エラーが発生しました。");
                    }
                }
                finally {
                    Loading.hide();
                }
            }
        };
        const onSaveRequest = async () => {
            return;
        };
        // 対象の部屋のチェックを外す処理
        const onSelectedDelete = (item) => {
            selected.value = selected.value.filter(v => v.orgNyukyokeiyakuNo !== item.orgNyukyokeiyakuNo);
        };
        // 個人情報ストアの選択部屋入居契約Noリストに選択した部屋を追加し、画面遷移
        const selectedBulkTaikyo = () => {
            TaikyoStore.selectedRooms = selected.value;
            // 画面遷移
            router.push({ name: "taikyo-room-list-next" });
        };
        // 退去ストアに対象の一括退去申請No.を追加し、退去通知入力画面に遷移
        const cardOrRowClick = (bulkTaikyoNo) => {
            TaikyoStore.taikyoNotificationEntity.bulkTaikyoNo = bulkTaikyoNo;
            router.push({ name: "taikyo-room-list-next" });
        };
        // ファイル関連処理
        const downloadFileByCode = async () => {
            try {
                // ローディング表示
                Loading.show();
                // ファイル取得
                const target = checkResult.fileList.find(x => x.fileCode === FileCode.TaikyoHasonTemplateFile);
                // ファイル未存在
                if (!target?.fileExists) {
                    await MsgBox.error(commonStore.messages.PE0001);
                    return;
                }
                // ファイル取得パラメータ設定
                getFilesCommand.fileList = [
                    {
                        fileNo: target.fileNo,
                        fileFetchKb: FileFetchKb.Server,
                        fileCode: target.fileCode,
                        fileExists: target.fileExists
                    }
                ];
                // ZIP(ファイル)取得
                const zipBlob = await api.common.getFiles(getFilesCommand);
                // Zip内のファイル取得
                const fileData = await FileHelper.getFileFromZip(zipBlob, target.fileNo);
                // ZIP内未存在
                if (!fileData) {
                    await MsgBox.error(commonStore.messages.PE0001);
                    return;
                }
                // ダウンロード
                FileHelper.downloadBlob(fileData.blob, fileData.fileName);
            }
            catch (ex) {
                log.error("downloadFileByCode", ex);
                // エラー表示
                MsgBox.error(ex?.data?.errorMessage ?? ex?.message ?? "エラーが発生しました。");
            }
            finally {
                // ローディング非表示
                Loading.hide();
            }
        };
        //全選択チェックボックスの押下時処理
        const changeAll = (isAllSelected) => {
            if (isAllSelected) {
                selected.value = sortResults.value;
            }
            else {
                selected.value = [];
            }
        };
        //ペインを閉じないままタブ切替した場合、ペインを強制的に閉じる
        watch(() => tab.value, () => {
            confirmPane.value = false;
        });
        // 一時保存一覧が空になった場合、新規申請タブに切り替える
        watch(() => saveList.value.length, length => {
            if (length === 0 && tab.value === "save") {
                tab.value = "entry";
            }
        });
        initView();
        return {
            router,
            pageName,
            PageID,
            isSaveList,
            tab,
            selected,
            condition,
            valid,
            pagination,
            entryColumns,
            viewResults,
            saveList,
            saveColumns,
            confirmPane,
            paneLabels,
            myPageStore,
            RoomSb,
            onSearch,
            onRequest,
            changeAll,
            onSelectedDelete,
            selectedBulkTaikyo,
            onSaveDelete,
            cardOrRowClick,
            onSaveRequest,
            downloadFileByCode,
            sortResults,
            viewSelectRoomSort
        };
    }
});
